import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Pencil, Plus, ArrowRightLeft, Trash2 } from 'lucide-react'
import type { Client, ClientStatus } from '../types'
import type { Point } from '@/modules/points/types'
import type { ClientBenefitHistoryItem, BenefitVoucher, AvailableBenefitTier } from '@/modules/benefits/types'
import { ConfirmDialog } from '@/shared/components/ui/molecules/ConfirmDialog'
import { Can } from '@/modules/auth/components/Can'
import { Permission } from '@/modules/auth/permissions/permissions'
import { ClientBenefitsSection } from './ClientBenefitsSection'

interface ClientDetailProps {
  client: Client
  status: ClientStatus | null
  points: Point[]
  pointsLoading: boolean
  onDeleteClient: () => void
  onInvalidatePoint: (pointId: number) => void
  isDeleting: boolean
  branchLabel?: string | null
  allBenefits: ClientBenefitHistoryItem[]
  vouchers: BenefitVoucher[]
  activeBenefitsCount: number
  usedBenefitsCount: number
  expiredBenefitsCount: number
  clientCategory: string | null
  availableTiers: AvailableBenefitTier[]
  benefitsLoading: boolean
  availableLoading: boolean
}

function getCategoryColor(category: string | null): string {
  switch (category?.toLowerCase()) {
    case 'oro':
      return 'bg-yellow-100 text-yellow-800'
    case 'marmol':
      return 'bg-gray-100 text-gray-800'
    case 'bronce':
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-blue-100 text-blue-800'
  }
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ClientDetail({
  client,
  status,
  points,
  pointsLoading,
  onDeleteClient,
  onInvalidatePoint,
  isDeleting,
  branchLabel,
  allBenefits,
  vouchers,
  activeBenefitsCount,
  usedBenefitsCount,
  expiredBenefitsCount,
  clientCategory,
  availableTiers,
  benefitsLoading,
  availableLoading,
}: ClientDetailProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [pointToInvalidate, setPointToInvalidate] = useState<Point | null>(null)

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {client.name} {client.last_name}
          </h1>
          {status?.category && (
            <span
              className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(status.category)}`}
            >
              {status.category}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Link
            to={`/points/add?clientId=${client.id}`}
            className="btn btn-primary flex items-center gap-1.5"
          >
            <Plus size={16} />
            Agregar Puntos
          </Link>
          <Link
            to={`/points/redeem?clientId=${client.id}`}
            className="btn btn-secondary flex items-center gap-1.5"
          >
            <ArrowRightLeft size={16} />
            Canjear
          </Link>
          <Link
            to={`/clients/${client.id}/edit`}
            className="btn btn-secondary flex items-center gap-1.5"
          >
            <Pencil size={16} />
            Editar
          </Link>
          <Can permission={Permission.CLIENTS_DELETE}>
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="btn bg-red-600 text-white hover:bg-red-700 flex items-center gap-1.5"
            >
              <Trash2 size={16} />
              Eliminar
            </button>
          </Can>
        </div>
      </div>

      {/* Client info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-sm text-gray-500">Puntos disponibles</p>
          <p className="text-3xl font-bold text-primary-600">
            {status?.available_points ?? 0}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">RUT</p>
          <p className="text-lg font-semibold">{client.rut}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Email</p>
          <p className="text-lg font-semibold">{client.email}</p>
        </div>
      </div>

      {/* Full details */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Información del cliente</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Teléfono</p>
            <p className="font-medium">{client.phone_number || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Comuna</p>
            <p className="font-medium">{client.commune || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Género</p>
            <p className="font-medium">{client.gender || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Fecha de nacimiento</p>
            <p className="font-medium">{formatDate(client.birthdate)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Sucursal</p>
            <p className="font-medium">{branchLabel || `#${client.id_branch_office}`}</p>
          </div>
          {client.client_test && (
            <div>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                Cliente de prueba
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Points history */}
      <div className="card">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Historial de transacciones</h2>
        </div>
        {pointsLoading ? (
          <div className="animate-pulse p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded" />
            ))}
          </div>
        ) : points.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay transacciones registradas
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">ID</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Fecha</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Tipo</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">Puntos</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">Usados</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700">Monto</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Comentario</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Expiración</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Estado</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {points.map((point) => (
                  <tr key={point.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/points/${point.id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {point.id}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{formatDateTime(point.datetime_transaction)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          point.transaction_type === 'add'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {point.transaction_type === 'add' ? 'Acumulación' : 'Canje'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{point.points}</td>
                    <td className="px-4 py-3 text-right">{point.used_points}</td>
                    <td className="px-4 py-3 text-right">
                      ${point.amount.toLocaleString('es-CL')}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{point.comment || '-'}</td>
                    <td className="px-4 py-3">{formatDate(point.datetime_expiration)}</td>
                    <td className="px-4 py-3">
                      {point.is_valid ? (
                        <span className="text-green-600 text-xs font-medium">Válido</span>
                      ) : (
                        <span className="text-red-600 text-xs font-medium">Anulado</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {point.is_valid && point.used_points === 0 && point.transaction_type === 'add' && (
                        <button
                          onClick={() => setPointToInvalidate(point)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Anular transacción"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Benefits section */}
      <ClientBenefitsSection
        allBenefits={allBenefits}
        vouchers={vouchers}
        activeCount={activeBenefitsCount}
        usedCount={usedBenefitsCount}
        expiredCount={expiredBenefitsCount}
        availableTiers={availableTiers}
        clientCategory={clientCategory}
        isLoading={benefitsLoading}
        availableLoading={availableLoading}
      />

      {/* Delete client dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Eliminar cliente"
        message={`Está a punto de eliminar al cliente ${client.name} ${client.last_name}. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={() => {
          setShowDeleteDialog(false)
          onDeleteClient()
        }}
        onCancel={() => setShowDeleteDialog(false)}
        isLoading={isDeleting}
      />

      {/* Invalidate point dialog */}
      <ConfirmDialog
        isOpen={!!pointToInvalidate}
        title="Anular transacción"
        message={
          pointToInvalidate
            ? `Se anulará la transacción #${pointToInvalidate.id} de ${pointToInvalidate.points} puntos. Esta acción no se puede deshacer.`
            : ''
        }
        confirmLabel="Anular"
        onConfirm={() => {
          if (pointToInvalidate) {
            onInvalidatePoint(pointToInvalidate.id)
            setPointToInvalidate(null)
          }
        }}
        onCancel={() => setPointToInvalidate(null)}
      />
    </div>
  )
}
